import React, { useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  useColorModeValue,
  TableContainer,
  Flex,
  Button,
  Select,
  HStack,
  IconButton
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Transaction } from '../services/blindboxService';

interface TransactionListProps {
  transactions: Transaction[];
  title?: string;
}

const TransactionList: React.FC<TransactionListProps> = ({ 
  transactions,
  title = 'Recent Transactions'
}) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Calculate total pages
  const totalPages = Math.ceil(transactions.length / itemsPerPage);
  
  // Get current transactions
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTransactions = transactions.slice(indexOfFirstItem, indexOfLastItem);
  
  // Change page
  const goToPage = (pageNumber: number) => {
    setCurrentPage(Math.max(1, Math.min(pageNumber, totalPages)));
  };
  
  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Format transaction type to display
  const formatTransactionType = (type: string) => {
    switch (type) {
      case 'blindbox_purchase':
        return 'BlindBox Purchase';
      case 'liquidity_add':
        return 'Add Liquidity';
      case 'liquidity_remove':
        return 'Remove Liquidity';
      case 'rewards_distribution':
        return 'Rewards Distribution';
      case 'rewards_claim':
        return 'Claim Rewards';
      default:
        return type.replace('_', ' ');
    }
  };

  // Get badge color based on transaction type
  const getTransactionBadgeColor = (type: string) => {
    switch (type) {
      case 'blindbox_purchase':
        return 'purple';
      case 'liquidity_add':
        return 'green';
      case 'liquidity_remove':
        return 'red';
      case 'rewards_distribution':
        return 'blue';
      case 'rewards_claim':
        return 'orange';
      default:
        return 'gray';
    }
  };

  return (
    <Box
      rounded="lg"
      bg={useColorModeValue('white', 'gray.700')}
      boxShadow="sm"
      p={4}
    >
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Text fontSize="lg" fontWeight="bold">
          {title} ({transactions.length})
        </Text>
        
        <HStack spacing={2}>
          <Text fontSize="sm">Show:</Text>
          <Select 
            size="sm" 
            width="70px"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1); // Reset to first page when changing items per page
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Select>
        </HStack>
      </Flex>
      
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Type</Th>
              <Th>Amount</Th>
              <Th>Reward</Th>
              <Th>User</Th>
              <Th>Time</Th>
            </Tr>
          </Thead>
          <Tbody>
            {currentTransactions.length > 0 ? (
              currentTransactions.map((transaction) => (
                <Tr key={transaction.id}>
                  <Td>
                    <Badge colorScheme={getTransactionBadgeColor(transaction.type)}>
                      {formatTransactionType(transaction.type)}
                    </Badge>
                    {transaction.item && (
                      <Badge ml={2} colorScheme="teal">
                        {transaction.item.name}
                      </Badge>
                    )}
                  </Td>
                  <Td>
                    {transaction.amount >= 0 ? '+' : ''}
                    {transaction.amount.toFixed(2)} SOL
                  </Td>
                  <Td>
                    {transaction.item ? (
                      <Text fontWeight="medium" color={transaction.item.value > Math.abs(transaction.amount) ? "green.500" : "red.500"}>
                        {transaction.item.value.toFixed(2)} SOL
                      </Text>
                    ) : (
                      <Text fontSize="xs" color="gray.500">N/A</Text>
                    )}
                  </Td>
                  <Td>
                    <Text fontSize="xs" fontFamily="monospace">
                      {transaction.address === 'system' 
                        ? 'SYSTEM' 
                        : `${transaction.address.substring(0, 6)}...${transaction.address.substring(transaction.address.length - 4)}`
                      }
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="xs">
                      {formatTimestamp(transaction.timestamp)}
                    </Text>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  No transactions found
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
      
      {totalPages > 1 && (
        <Flex justifyContent="space-between" mt={4} alignItems="center">
          <Text fontSize="sm">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, transactions.length)} of {transactions.length} transactions
          </Text>
          
          <HStack>
            <IconButton
              aria-label="Previous page"
              icon={<ChevronLeftIcon />}
              size="sm"
              onClick={() => goToPage(currentPage - 1)}
              isDisabled={currentPage === 1}
            />
            
            {/* Page number buttons - show 5 pages max with current in the middle if possible */}
            {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
              let pageNum: number;
              if (totalPages <= 5) {
                // If 5 or fewer pages, show all page numbers
                pageNum = idx + 1;
              } else if (currentPage <= 3) {
                // If near the start, show first 5 pages
                pageNum = idx + 1;
              } else if (currentPage >= totalPages - 2) {
                // If near the end, show last 5 pages
                pageNum = totalPages - 4 + idx;
              } else {
                // Otherwise show current page in the middle
                pageNum = currentPage - 2 + idx;
              }
              
              return (
                <Button
                  key={idx}
                  size="sm"
                  colorScheme={currentPage === pageNum ? "brand" : "gray"}
                  variant={currentPage === pageNum ? "solid" : "outline"}
                  onClick={() => goToPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
            
            <IconButton
              aria-label="Next page"
              icon={<ChevronRightIcon />}
              size="sm"
              onClick={() => goToPage(currentPage + 1)}
              isDisabled={currentPage === totalPages}
            />
          </HStack>
        </Flex>
      )}
    </Box>
  );
};

export default TransactionList; 